import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Typography, CircularProgress, Alert, Button, Link } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { steamCallback } from '../../services/api';

const SteamCallback: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showLinkSuccess, setShowLinkSuccess] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const goToSettings = () => {
    // Steam連携タブへ直接遷移（?tab=steam）
    navigate('/profile/settings?tab=steam', {
      state: { message: 'Steamアカウントの連携に成功しました' }
    });
  };

  useEffect(() => {
    const handleSteamCallback = async () => {
      try {
        // Steam認証からのすべてのパラメータを取得
        const params = new URLSearchParams(location.search);

        // パラメータが空の場合はエラー
        if (!params.toString()) {
          throw new Error('認証パラメータが見つかりません');
        }

        // URLパラメータからモードを取得
        const isLoginMode = params.get('mode') === 'login';

        const response = await steamCallback(params.toString());

        if (isLoginMode) {
          // ログインモードの場合、トークンを保存
          if (response.data.access_token) {
            localStorage.setItem('token', response.data.access_token);
            localStorage.setItem('refreshToken', response.data.refresh_token);
            localStorage.setItem('user', JSON.stringify(response.data.user));

            // ログイン成功イベントを発火
            window.dispatchEvent(new Event('login'));

            // ホームページにリダイレクト
            setTimeout(() => {
              navigate('/', {
                state: { message: 'Steamアカウントでログインしました' }
              });
            }, 2000);
          }
        } else {
          // 連携モードの場合
          if (response.data.steam_id) {
            if (response.data.sync_defaults_enabled) {
              // 初回連携：同期設定がデフォルトONになったことを案内するカードを表示
              // （自動リダイレクトはせず、ユーザーが読んでからボタンで遷移）
              setShowLinkSuccess(true);
            } else {
              // 再連携など：従来どおり設定ページへ
              setTimeout(goToSettings, 2000);
            }
          } else {
            throw new Error('Steam IDの取得に失敗しました');
          }
        }

      } catch (err: any) {
        console.error('Steam認証エラー:', err);
        setError(err.response?.data?.error || err.message || 'Steam認証に失敗しました');
      } finally {
        setLoading(false);
      }
    };

    handleSteamCallback();
  }, [location, navigate]);

  // 有効化された設定の一覧（緑チェックで表示）
  const enabledItems = [
    '自動同期（毎日1回）',
    'ステータスの自動更新',
    'ウィッシュリストの同期',
  ];

  // MUI Dialog は使わず、ページ上に自前の最小カードとして描画する。
  // （DialogContent の overflow-y:auto に伴う overflow-x 自動auto化や Paper 幅競合を回避するため）
  const linkSuccessCard = (
    <Box
      sx={{
        width: '100%',
        maxWidth: 460,
        boxSizing: 'border-box',
        p: 3,
        borderRadius: 3,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: 6,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2.5 }}>
        {/* Steamマークの成功アニメーション：ポップイン＋青いパルスリング。
            リングの scale 拡大が外へ染み出さないよう、最大径を内包する枠で包む */}
        <Box
          sx={{
            width: 112,
            height: 112,
            mb: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ position: 'relative', width: 72, height: 72 }}>
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                borderRadius: '50%',
                border: '2px solid #66c0f4',
                '@keyframes steamRingPulse': {
                  '0%': { transform: 'scale(0.85)', opacity: 0.7 },
                  '100%': { transform: 'scale(1.4)', opacity: 0 },
                },
                animation: 'steamRingPulse 1.8s ease-out infinite',
              }}
            />
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #1b2838, #2a475e)',
                boxShadow: '0 4px 20px rgba(102,192,244,0.35)',
                '@keyframes steamPopIn': {
                  '0%': { transform: 'scale(0.6)', opacity: 0 },
                  '60%': { transform: 'scale(1.08)' },
                  '100%': { transform: 'scale(1)', opacity: 1 },
                },
                animation: 'steamPopIn .5s cubic-bezier(.2,.8,.2,1)',
              }}
            >
              <Box
                component="img"
                src="/icons/service/steam.svg"
                alt="Steam"
                sx={{ width: 40, height: 40 }}
              />
            </Box>
          </Box>
        </Box>
        <Typography variant="h6" align="center">
          自動同期を有効にしました
        </Typography>
      </Box>

      <Box sx={{ mb: 2 }}>
        {enabledItems.map((label) => (
          <Box key={label} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.75 }}>
            <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main', flexShrink: 0, mt: '2px' }} />
            <Typography variant="body2" sx={{ minWidth: 0 }}>{label}</Typography>
          </Box>
        ))}
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        これらの設定は「設定 ＞ Steam連携」からいつでも変更できます。
      </Typography>

      {/* プライバシー設定の案内（同期失敗を防ぐため目立たせる） */}
      <Alert severity="warning" sx={{ mb: 2 }}>
        <Typography variant="body2" fontWeight={600} gutterBottom>
          同期にはSteamの公開設定が必要です
        </Typography>
        <Box component="ul" sx={{ pl: 2, m: 0 }}>
          <Typography component="li" variant="caption" color="text.secondary">
            「ゲームの詳細」を「公開」に設定
          </Typography>
          <Typography component="li" variant="caption" color="text.secondary">
            「総プレイ時間を常に非公開にする」のチェックを外す
          </Typography>
        </Box>
        <Link
          href="https://steamcommunity.com/my/edit/settings"
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            mt: 1,
            fontSize: '0.75rem',
            fontWeight: 600,
          }}
        >
          Steamのプライバシー設定を開く
          <OpenInNewIcon fontSize="small" />
        </Link>
      </Alert>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button onClick={goToSettings} variant="contained" color="primary">
          閉じる
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '50vh',
      p: 3
    }}>
      {loading ? (
        <>
          <CircularProgress size={60} sx={{ mb: 3 }} />
          <Typography variant="h6">
            {new URLSearchParams(location.search).get('mode') === 'login'
              ? 'PULSEライブラリとの連携を確認中...'
              : 'Steamアカウントの連携処理中...'}
          </Typography>
        </>
      ) : error ? (
        <Alert severity="error" sx={{ maxWidth: 500 }}>
          {error}
        </Alert>
      ) : showLinkSuccess ? (
        linkSuccessCard
      ) : (
        <Alert
          severity="success"
          sx={{
            maxWidth: 500,
            background: 'linear-gradient(135deg, rgba(23, 26, 33, 0.95), rgba(42, 71, 94, 0.95))',
            color: '#ffffff',
            border: '1px solid rgba(102, 192, 244, 0.2)',
            backdropFilter: 'blur(8px)',
            '& .MuiAlert-icon': {
              color: '#66c0f4'
            }
          }}
        >
          {new URLSearchParams(location.search).get('mode') === 'login'
            ? 'ログインに成功しました。正常にPULSEライブラリと連携されています。'
            : 'Steamアカウントの連携に成功しました。'
          }
        </Alert>
      )}
    </Box>
  );
};

export default SteamCallback;
